import { fromUnixTime } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { mediaQueries } from '@general/breakpoints';
import colors from '@general/colors';
import { getDateRangeTextLong, getTimeRangeText } from '@general/dates';
import fonts from '@general/fonts';
import { decodeHtml, removeHtmlFromText, truncateText } from '@general/helpers/text.helpers';
import CardChip from '@molecules/CardChip';
import { H4, LabelSmall, LinkSmall } from '@styled';
import { useTrusteeEventSessionModal } from './TrusteeEventSessionModal';

const Container = styled.div`
    border-bottom: 1px solid ${colors.lightGray};
    padding-bottom: 16px;
    padding-top: 32px;

    @media ${mediaQueries.md} {
        display: flex;
    }
`;

const Column1 = styled.div`
    margin-bottom: 16px;

    @media ${mediaQueries.md} {
        margin-bottom: 0;
        width: 20%;
    }
`;

const Column2 = styled.div`
    padding-right: 32px;
    position: relative;

    @media ${mediaQueries.md} {
        width: 80%;
    }
`;

const TitleStyled = styled(H4)`
    transition: color 0.2s ease-in-out;

    &:hover {
        color: ${colors.primary};
    }
`;

const Description = styled.p`
    font-family: ${fonts.sans};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    margin: 0;

    p,
    ul {
        font-family: ${fonts.sans};
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 21px;
        margin: 0;
    }
`;

const TrusteeEventSessionListItem = ({
    fees,
    id,
    registrantClass,
    title,
    sessionStatus,
    sessionStartUnix,
    sessionEndUnix,
    sessionRoom,
    description,
    event,
    isRegistered,
}) => {
    const { openModal: openSessionDetailModal } = useTrusteeEventSessionModal({
        fees,
        id,
        isRegistered,
        registrantClass,
        title,
        sessionStatus: sessionStatus?.[0],
        sessionStartUnix,
        sessionEndUnix,
        sessionRoom,
        description,
        eventCode: event?.event_code,
    });

    const isPast = sessionEndUnix < Date.now() / 1000;

    return (
        <Container className="event-session-card">
            <Column1>
                <LabelSmall className="d-block mb-2">{getDateRangeTextLong(fromUnixTime(sessionStartUnix))}</LabelSmall>
                <LabelSmall className="d-block">
                    {getTimeRangeText(fromUnixTime(sessionStartUnix), fromUnixTime(sessionEndUnix))}
                </LabelSmall>
            </Column1>

            <Column2>
                <div className="">
                    <TitleStyled
                        as="a"
                        href="#"
                        onClick={e => {
                            e.preventDefault();
                            openSessionDetailModal();
                        }}
                    >
                        {title}
                    </TitleStyled>
                </div>

                {fees && !isRegistered && !isPast && (
                    <div className="mt-0.25rem">
                        <CardChip color="red">Registration Open</CardChip>
                    </div>
                )}

                {fees && isRegistered && !isPast && (
                    <div className="mt-0.25rem">
                        <CardChip color="emerald">Already Registered</CardChip>
                    </div>
                )}

                {sessionRoom && <LabelSmall className="d-block mt-0.5rem">{sessionRoom}</LabelSmall>}
                {description && (
                    <Description className="mt-0.5rem">
                        {truncateText(decodeHtml(removeHtmlFromText(description)), 120)}
                    </Description>
                )}

                {fees && !isRegistered && !isPast && (
                    <div className="flex">
                        <LinkSmall
                            className="block mt-0.5rem"
                            href={`/cart/view?event=${event.event_code}&session=${id}`}
                        >
                            <span>Register Now</span>
                        </LinkSmall>
                    </div>
                )}
            </Column2>
        </Container>
    );
};

TrusteeEventSessionListItem.propTypes = {
    fees: PropTypes.arrayOf(PropTypes.object),
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    isRegistered: PropTypes.bool.isRequired,
    registrantClass: PropTypes.arrayOf(PropTypes.string).isRequired,
    title: PropTypes.string,
    sessionStatus: PropTypes.arrayOf(PropTypes.string),
    sessionStartUnix: PropTypes.number,
    sessionEndUnix: PropTypes.number,
    sessionRoom: PropTypes.string,
    description: PropTypes.string,
    event: PropTypes.shape({
        event_code: PropTypes.string,
    }),
};

export default TrusteeEventSessionListItem;
